import { FC, useEffect } from "react";
import { Trans } from "react-i18next";
import { useAppSelector } from "store/hooks";
import { userMetadataSelector } from "store/selectors";

import { AiAssistantChatHistory, AiAssistantJSONChatMessageType } from "types/aiAssistant";
import { hasAdminRights } from "permissions/helpers/shared";

import { Text } from "uikit";

import {
  adminAiAssistantSuggestedActionsConfig,
  employeeAiAssistantSuggestedActionsConfig,
} from "../constants";
import { IAiAssistantSuggestedActionConfig } from "../types";
import { AiChatItem } from "./AiChatItem";
import {
  AiChatSuggestedActionContainer,
  AiChatSuggestedActions,
  Container,
  StyledDotLoader,
} from "./styles";

interface IProps {
  data: AiAssistantChatHistory;
  isLoading: boolean;
  onSuggestedActionClick: (action: IAiAssistantSuggestedActionConfig) => void;
  onSubmitButtonClick: (actionType: AiAssistantJSONChatMessageType) => void;
  onCancelButtonClick: () => void;
}

export const AiChat: FC<IProps> = ({
  data,
  isLoading,
  onSuggestedActionClick,
  onSubmitButtonClick,
  onCancelButtonClick,
}) => {
  const currentUser = useAppSelector(userMetadataSelector);
  const shouldShowSuggestedAction = !data.length;
  const isAdmin = hasAdminRights(currentUser);

  useEffect(() => {
    const container = document.getElementById("ai-assistant-chat-container");
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, [data.length]);

  const renderSuggestedAction = () => {
    const suggestedActions = isAdmin
      ? adminAiAssistantSuggestedActionsConfig
      : employeeAiAssistantSuggestedActionsConfig;
    return (
      <AiChatSuggestedActions>
        {suggestedActions.map((item) => (
          <AiChatSuggestedActionContainer
            key={item.title}
            onClick={() => onSuggestedActionClick(item)}
            data-testid={`ai-assistant-suggested-action-${item.title}`}
          >
            <Text $fs="t12" $fw="semiBold" component="span">
              <Trans i18nKey={item.textKey} />
            </Text>
          </AiChatSuggestedActionContainer>
        ))}
      </AiChatSuggestedActions>
    );
  };

  return (
    <Container id="ai-assistant-chat-container">
      {data.map((item, index) => (
        <AiChatItem
          key={item.timestamp}
          data={item}
          isButtonDisabled={index !== data.length - 1}
          onSubmitButtonClick={onSubmitButtonClick}
          onCancelButtonClick={onCancelButtonClick}
          isLoadingAfterSubmit={isLoading}
        />
      ))}

      {shouldShowSuggestedAction && renderSuggestedAction()}
      {isLoading && <StyledDotLoader />}
    </Container>
  );
};
